// src\app\components\CheatSheetPdf_Company.js

import HOOK from "./hooks"
import pdfMake from "pdfmake/build/pdfmake.js"
import pdfFonts from "pdfmake/build/vfs_fonts.js"
import nLogo from "../assets/images/numerable-logo-2022-512w-8b.png"
import cLogo from "../assets/images/the350club-logo-2024-8b.png"
import { BASE, COLOR, COLUMN, COUNTRY, FORMATTING, JARGON, KIND, RADIO, TABLE_BIT, TENSE, TENSE_ENUM, POSITION_TYPE } from "../modules/constants"
import COLUMNS from "../modules/columns"
import { CHECKBOX } from "../modules/checkbox_constants"
import { formatDate, getAge, getCapacities, getCheckboxName, getNameProp } from "../modules/utilities"
import { docDefinition, fontSize, footer, get, getHeader, header, infoTable, initTableLayouts, sortArray, styles, width } from "./CheatSheetPdf_Common"

pdfMake.vfs = pdfFonts

//#region constants
const { center, left, right } = FORMATTING
const { greyText, darkGreyText, veryDarkGreyText } = COLOR.PDF
const tableLayouts = {}
//#endregion

const cheatSheetPdf = props => {
  // console.log("cheatSheetPdf props:", props)
  initTableLayouts(props.brandColor, tableLayouts)
  const document = createDocument(props)
  pdfMake.createPdf(document).open()
}

const createDocument = props => ({
  ...docDefinition,
  styles: styles(props),
  header: header(props),
  content: getContent(props),
  images: {
    numerableLogo: nLogo,
    the350ClubLogo: cLogo,
  },
  footer: function (currentPage, pageCount) { return footer(currentPage, pageCount) },
})

function getContent(props) {
  // console.log("getContent props: ", props)

  const content = [
    titleTable(props),
    coreTable(props),
    rolesLabel(props),
  ]

  if (props.committees)
    content.push(committeesTable({ ...props, content, tenseEnum: TENSE_ENUM.CURRENT }))
  else {
    props.untilColumn = !!(props.orderSet & TABLE_BIT.RESIGNED)

    // if the company has ended, don't show current directors table
    if (!!!props.hubData.toDate) {
      getTenseContent({ ...props, content, tenseEnum: TENSE_ENUM.CURRENT })
      getTenseContent({ ...props, content, tenseEnum: TENSE_ENUM.NOTICE })
      getTenseContent({ ...props, content, tenseEnum: TENSE_ENUM.FUTURE })
    }

    getTenseContent({ ...props, content, tenseEnum: TENSE_ENUM.PAST })
  }

  content.push(detailsTable(props))
  content.push(infoTable({ ...props, tableLayouts }))

  return content
}

const getTenseContent = props => {
  const { content, tenseEnum } = props
  if (!!(CHECKBOX[HOOK.TENSE][tenseEnum].BIT & props.checkboxes[HOOK.TENSE]))
    content.push(props.radio[BASE.COMPANY] === RADIO[BASE.COMPANY].CONNECTIONS
      ? connectionsTable(directorsTableProps(props, tenseEnum))
      : directorsTable(directorsTableProps(props, tenseEnum))
    )
}

const directorsTableProps = (props, tenseEnum) => ({
  ...props,
  tenseBits: (CHECKBOX[HOOK.TENSE][tenseEnum].BIT & props.checkboxes[HOOK.TENSE]),
  tenseEnum,
})

const titleTable = props => {
  const { lseProps } = props.hubData
  return {
    style: "titleTable",
    layout: tableLayouts.title,
    table: {
      // headerRows: 1,
      widths: ["80%", "*"],
      body: props.maxLseData && !!lseProps
        ?
        [
          [
            { text: getName(props), fontSize: fontSize.huge, bold: true, color: darkGreyText },
            { text: JARGON.CHEAT_SHEET + " at " + get.formattedDate(), fontSize: fontSize.small, alignment: right },
          ],
          [{ text: lseProps.description, colSpan: 2, fontSize: fontSize.medium, lineHeight: 1.4 }],
          [{
            text: [
              { text: "Company website", link: prefixHttps(lseProps.websiteLinks["home"]), style: "link" },
              { text: " / " },
              { text: "Annual report", link: prefixHttps(lastAccountsLink(props.hubData)), style: "link" },
              { text: props.hubData.companyRegCountry === "GB" ? " / " : "" },
              { text: props.hubData.companyRegCountry === "GB" ? "Companies House" : "", link: prefixHttps(companiesHouseLink(props.hubData)), style: "link" },
              { text: " / " },
              { text: "Map of address", link: prefixHttps(props.hubData.mapLink), style: "link" },
            ], colSpan: 2,
          }]
        ]
        : // non LSE company - so must be on Companies House
        [
          [
            { text: getName(props), fontSize: fontSize.huge, bold: true, color: darkGreyText },
            { text: JARGON.CHEAT_SHEET + " at " + get.formattedDate(), fontSize: fontSize.small, alignment: right },
          ],
          [{
            text: [
              // Set style to null if link is null
              { text: "Last accounts", link: prefixHttps(lastAccountsLink(props.hubData)), style: lastAccountsLink(props.hubData) ? "link" : null },
              { text: " / " },
              { text: "Companies House", link: prefixHttps(companiesHouseLink(props.hubData)), style: "link" },
              { text: " / " },
              { text: "Map of address", link: prefixHttps(props.hubData.mapLink), style: "link" },
            ], colSpan: 2,
          }]
        ],
    },
  }
}

// Prefix link with "https://"
const prefixHttps = link => link
  ? link.startsWith("http")
    ? link
    : `https://${link}`
  : null

/***** core tables *****/
const coreTable = props =>
  props.maxLseData && !!props.hubData.lseProps
    ? lseCoreTable(props)
    : null

const lseCoreTable = props => {
  const { PDF: text } = JARGON
  const { hubData: { lseProps } } = props

  return {
    style: "coreTable",
    layout: tableLayouts.core,
    table: {
      widths: ["*", 0, "*", 0, "*", 0, "*", 0, "*", 0, "*"],
      body: [
        [{}, {}, {}, {}, {}, {}, {}, {}, {}, {}, {}],
        [
          {
            text: lseProps.annualReport?.dates?.accountsDate
              ? `Revenue (${formatDate(lseProps.annualReport.dates.accountsDate, true)})`
              : `Revenue`, style: "coreLegend"
          },
          {},
          { text: text.mrktCap, style: "coreLegend" },
          {},
          { text: text.employees, style: "coreLegend" },
          {},
          { text: text.directors, style: "coreLegend" },
          {},
          getHeader({ sortId: COLUMN.LSE_INDEX, prefix: `${JARGON.LSE} `, style: "coreLegend" }),
          {},
          getHeader({ sortId: COLUMN.EPIC, prefix: `${JARGON.LSE} `, style: "coreLegend" }),
        ],
        [
          { text: getProp(props.dataItems.fiscal, "revenue"), style: "coreValue" },
          {},
          { text: getProp(props.dataItems.fiscal, "market cap."), style: "coreValue" },
          {},
          { text: get.numberToLocalString(lseProps.employees), style: "coreValue" },
          {},
          { text: directorsCount(props), style: "coreValue" },
          {},
          { text: get.index(lseProps.indexBit), style: "coreValue" },
          {},
          { text: lseProps.epics[0], style: "coreValue" },
        ],
      ]
    },
  }
}

const rolesLabel = props => {
  const cmtes = props.hubData.lseProps?.cmtes
  // Return null if there are no committee members
  // Reminder: cmtes[0], cmtes[1] and cmtes[2] represent audit, rem and nom committees
  // eg: where cmtes[0] === [1, 4], company has 1 audit chair and 4 audit members
  return props.committees && [cmtes[0], cmtes[1], cmtes[2]].every(i => i.every(j => j === 0))
    ? null
    : {
      style: "coreLabel",
      layout: tableLayouts.rolesLabel,
      table: {
        widths: ["*", "*"],
        body: [
          props.committees
            ? [{ text: "Committee Roles" }, { text: absentCommittees(props), fontSize: fontSize.small, alignment: right }]
            : [{ text: "Board Roles" }, {}],
        ]
        // widths: ["*"],
        // body: [
        //   props.committees
        //     ? [{ text: [{ text: "Committee Roles" }, { text: absentCommittees(props), fontSize: fontSize.small, alignment: left }] }]
        //     : [{ text: [{ text: "Board Roles" }, {}] }]
        // ]
      },
    }
}

// Returns text for absent committees
// eg "(ABERFORTH SMALLER COMPANIES TRUST PLC has no remuneration and no nomination committees)"
const absentCommittees = props => {
  var text = ''
  var absent = 0
  const { hubData } = props
  const { cmtes } = hubData.lseProps

  // Ast least one committee is absent
  if ([cmtes[0][0], cmtes[1][0], cmtes[2][0]].includes(-1)) {
    // Add the company name
    text = `${hubData.name}`

    if (cmtes[0][0] === -1) {
      text += ` has no audit committee`
      absent++
    }

    if (cmtes[1][0] === -1) {
      text += absent === 1 ? ' or' : ' has no'
      text += ` remuneration`
      absent++
    }

    if (cmtes[2][0] === -1) {
      text += absent === 1 ? ' or' : ' has no'
      text += ` nomination`
      absent++
    }

    text += ' committee'
  }

  return text
}

const committeesTable = props => {
  const body = getCommittees(props)

  return body
    ? ({
      style: "bodyTable",
      layout: tableLayouts.committees,
      table: {
        widths: ["*", "*", "*", width.committee, width.committee, width.committee, width.committee, width.committee, width.committee],
        headerRows: 1,
        body,
      },
    })
    : null
}

const getCommittees = props => {
  // console.log("getDirectors props:", props)
  const { orderSet, tableOrder } = props

  let nodes = props.nodes
    .filter(i => i.level > 0) // all but the hub node

  const array = []

  nodes.forEach(node => {
    // Get all connections where childId === node.id
    // NB: The connections have already been filtered by parentId === hubNode id
    // @ts-ignore
    const connection = Object.filter(props.connections, c => c.childId === node.id)
    const capacities = getCapacities(connection, node.id)

    getCommitteeRoles({ ...props, array, capacities: capacities?.officer || [], node })
  })

  if (array.length) {
    const headers = getCommitteesHeaders()

    sortArray({ array, headers, orderSet, tableOrder, kind: KIND.NEXUS.COMPANY.COMMITTEES })
    return [headers, ...array]
  } else
    return null
}

//#region directors table
const directorsTable = props => {
  const { untilColumn } = props
  const body = getDirectors(props)

  return body
    ? ({
      style: "bodyTable",
      layout: tableLayouts.body,
      table: {
        widths: props.maxLseData && !!props.hubData.lseProps
          ? untilColumn
            ? ["*", "*", width.date, width.date, width.tenure, width.remuneration, width.age, width.gender]
            : ["*", "*", width.date, width.tenure, width.remuneration, width.age, width.gender]
          : untilColumn
            ? ["*", "*", width.date, width.date, width.tenure, width.age]
            : ["*", "*", width.date, width.tenure, width.age],
        headerRows: 1,
        body,
      },
    })
    : null
}

const getDirectors = props => {
  // console.log("getDirectors props:", props)
  const { checkboxes, orderSet, tableOrder, tenseEnum } = props

  let nodes = props.nodes
    .filter(i => i.level > 0) // all but the hub node
    .filter(i => !!(checkboxes[HOOK.TENSE] & i.tenseBits)) // nodes that match the supplied tense
  const array = []

  nodes.forEach(node => {
    // Get all connections where childId === node.id
    // NB: The connections have already been filtered by parentId === hubNode id
    // @ts-ignore
    const connection = Object.filter(props.connections, c => c.childId === node.id)
    const capacities = getCapacities(connection, node.id)

    getDirectorRoles({ ...props, array, capacities: capacities?.officer || [], node })

    // only list PSC roles if role PSC checkbox is selected
    if (!!(checkboxes[HOOK.POSITION] & CHECKBOX[HOOK.POSITION][POSITION_TYPE.PSC].BIT))
      getDirectorRoles({ ...props, array, capacities: capacities?.psc || [], node })
  })

  if (array.length) {
    const headers = getDirectorsHeaders({ props, orderSet, tenseEnum, })

    sortArray({ array, headers, orderSet, tableOrder, kind: KIND.NEXUS.COMPANY.BOARD })
    return [headers, ...array]
  } else
    return null
}

const getDirectorRoles = props => {
  const { hubData, node } = props
  const { entity } = node
  const { commonProps, personInfo } = entity

  // Handle legacy entity structure
  const { name } = commonProps ? commonProps : entity

  const rolesByTense = props.capacities.filter(c => c.tense === props.tenseEnum)

  rolesByTense.forEach(role => {
    const row = [
      { text: getNameProp.toPresent(name), color: veryDarkGreyText, sortValue: getNameProp.toSort(name) },
      {
        text: props.features.EnablePosition && role.position
          ? role.position.title
          : role.roleDescription
            ? role.roleDescription : "Officer",
        color: greyText,
        sortValue: get.positionSortValue(role.position.title),
      },
      { text: get.appointedToBoard(role.from, right), color: greyText, sortValue: get.fromSortValue(role.from) },
    ]

    if (props.untilColumn)
      row.push({ text: get.resignedFromBoard(hubData.toDate, role, right), color: greyText, sortValue: get.toDateSortValue(hubData.toDate) })
    row.push({ text: get.yearsAndMonths(role.from, role.until, right), color: greyText, sortValue: get.yearsTenureSortValue(role.days) },)

    if (props.maxLseData && !!hubData.lseProps)
      row.push({ text: get.remunerationObj(role.compensationGBP, right), color: greyText, sortValue: get.compensationSortValue(role.compensationGBP) })

    row.push({ text: getAgeObj(personInfo, right), color: greyText, sortValue: get.ageSortValue(personInfo) })

    if (props.maxLseData && !!hubData.lseProps)
      row.push({ text: getSexObj(personInfo, right), color: greyText, sortValue: getSexText(personInfo) })

    props.array.push(row)
  })
}

const getCommitteeRoles = props => {
  const { name } = props.node.entity
  const color = greyText

  // Boolean expressing absent committees
  const noAudCom = props.hubData.lseProps.cmtes[0][0] === -1
  const noRemCom = props.hubData.lseProps.cmtes[1][0] === -1
  const noNomCom = props.hubData.lseProps.cmtes[2][0] === -1

  props.capacities.forEach(role => {
    // console.log('role:', role)
    const row = [
      { text: getNameProp.toPresent(name), color: veryDarkGreyText, sortValue: getNameProp.toSort(name) },
      {
        text: props.features.EnablePosition && role.position
          ? role.position.title
          : role.roleDescription
            ? role.roleDescription : "Officer",
        color,
        sortValue: get.positionSortValue(role.position.title),
      },
      { text: get.yearsAndMonths(role.from, role.until, right) },
    ]

    const alignment = center
    rowPush({ alignment, cmte: role.cmtes.aud, color, row, style: getStyle(noAudCom) })
    rowPush({ alignment, cmte: role.cmtes.rem, color, row, style: getStyle(noRemCom) })
    rowPush({ alignment, cmte: role.cmtes.nom, color, row, style: getStyle(noNomCom) })

    props.array.push(row)
  })
}

const getStyle = cmte => cmte ? "noCommittee" : null

const rowPush = ({ alignment, cmte, color, row, style }) => {
  row.push({ text: get.committeeObj(cmte === 1, alignment), color, sortValue: cmte === 1 ? 1 : 2, style })
  row.push({ text: get.committeeObj(cmte === 2, alignment), color, sortValue: cmte === 2 ? 1 : 2, style })
}

const getCommitteesHeaders = () => {
  const style = "tableHeader"
  const columns = COLUMNS.LANDSCAPE[KIND.NEXUS.COMPANY.COMMITTEES]
  const cell = {}

  let alignment = right
  const name = getHeader({ sortId: COLUMN.OFFICER, style })
  const role = getHeader({ sortId: COLUMN.ROLE, style })
  const tenure = getHeader({ cell, sortId: COLUMN.TENURE, style, alignment })

  alignment = center
  const auditChair = getHeader({ cell, sortId: COLUMN.COMMITTEE.AUDIT.CHAIRS, style, alignment })
  const auditMembers = getHeader({ cell, sortId: COLUMN.COMMITTEE.AUDIT.MEMBER, style, alignment })
  const remChair = getHeader({ cell, sortId: COLUMN.COMMITTEE.REMUNERATION.CHAIRS, style, alignment })
  const remMember = getHeader({ cell, sortId: COLUMN.COMMITTEE.REMUNERATION.MEMBER, style, alignment })
  const nomChair = getHeader({ cell, sortId: COLUMN.COMMITTEE.NOMINATION.CHAIRS, style, alignment })
  const nomMember = getHeader({ cell, sortId: COLUMN.COMMITTEE.NOMINATION.MEMBER, style, alignment })

  const array2 = []
  for (const [key, value] of Object.entries(columns))
    if (value === true && cell[parseInt(key)])
      array2.push(cell[parseInt(key)])

  const array = [name, role, tenure]
  array.push(auditChair, auditMembers, remChair, remMember, nomChair, nomMember)


  return array
}

const getDirectorsHeaders = ({ props, orderSet, tenseEnum }) => {
  const style = "tableHeader"
  const alignment = right
  const name = getHeader({ sortId: COLUMN.OFFICER, prefix: `${TENSE[tenseEnum].NAME} `, style })
  const role = getHeader({ sortId: COLUMN.ROLE, style })
  const resigned = getHeader({ sortId: COLUMN.UNTIL, style, alignment })
  const appointed = getHeader({ sortId: COLUMN.FROM, style, alignment })
  const tenure = getHeader({ sortId: COLUMN.TENURE, style, alignment })
  const age = getHeader({ sortId: COLUMN.AGE, style, alignment })
  const sex = getHeader({ sortId: COLUMN.SEX, style, alignment })
  const remuneration = getHeader({ sortId: COLUMN.COMPENSATION, style, alignment })

  const array = [name, role, appointed]

  // NB: The resigned column will be set if past or notice checkboxes are set
  if (!!(orderSet & TABLE_BIT.RESIGNED))
    array.push(resigned)

  return props.maxLseData && !!props.hubData.lseProps
    ? [...array, tenure, remuneration, age, sex]
    : [...array, tenure, age]
}
//#endregion

//#region connections table
const connectionsTable = props => {
  // console.log('connectionsTable props:', props)
  const body = getConnections(props)

  return body
    ? ({
      style: "bodyTable",
      layout: tableLayouts.body,
      table: {
        widths: props.maxLseData
          ? props.untilColumn
            ? ["*", width.index, width.director, width.date, width.date, width.overlap]
            : ["*", width.index, width.director, width.date, width.overlap]
          : props.untilColumn
            ? ["*", width.director, width.date, width.date, width.overlap]
            : ["*", width.director, width.date, width.overlap],
        headerRows: 1,
        body,
      },
    })
    : null
}

const getConnections = props => {
  // console.log("getConnections props:", props)

  const { checkboxes, orderSet, tableOrder, tenseEnum, via } = props
  let nodes = props.nodes.filter(i => i.level > 1) // all but the hub node
  nodes = nodes.filter(i => !!(checkboxes[HOOK.TENSE] & i.tenseBits))
  nodes = nodes.filter(i => i.tense === tenseEnum)
  const array = []

  nodes.forEach(node => {
    const { entity, via } = node
    const indexBit = get.indexBit(node)

    const row = [{ text: getNameProp.toPresent(entity.name), color: veryDarkGreyText, sortValue: getNameProp.toSort(entity.name) }]

    if (props.maxLseData)
      row.push({
        text: get.index(indexBit),
        color: greyText,
        sortValue: get.indexSortValue(indexBit)
      })

    row.push({ text: getNameProp.toPresent(via.name), color: greyText, sortValue: getNameProp.toSort(via.name) })

    row.push({
      text: get.appointedToBoard(node.connectionStart, right),
      color: greyText,
      sortValue: get.fromSortValue(node.connectionStart)
    })

    if (props.untilColumn)
      row.push({
        text: get.resignedFromBoard(node.connectionEnd, { tense: node.tense }, right),
        color: greyText,
        sortValue: get.toDateSortValue(node.connectionEnd)
      })

    row.push({
      text: get.yearsAndMonths(node.connectionOverlapStart, node.connectionOverlapEnd, right),
      color: greyText,
      sortValue: get.yearsTenureSortValue(node.connectionOverlapDuration)
    })

    array.push(row)
  })

  if (array.length) {
    const headers = getConnectionsHeaders({ props, orderSet, tenseEnum })

    sortArray({ array, headers, orderSet, tableOrder, kind: KIND.NEXUS.COMPANY.BOARD })
    return [headers, ...array]
  } else
    return null
}

const getConnectionsHeaders = ({ props, orderSet, tenseEnum }) => {
  const style = "tableHeader"
  const alignment = right
  const name = getHeader({ sortId: COLUMN.CONNECTED_CO, prefix: `${TENSE[tenseEnum].NAME} `, style })
  const index = getHeader({ sortId: COLUMN.LSE_INDEX, prefix: `${JARGON.LSE} `, style })
  const via = getHeader({ sortId: COLUMN.VIA_PERSON, style })
  const appointed = getHeader({ sortId: COLUMN.FROM, style, alignment })
  const resigned = getHeader({ sortId: COLUMN.UNTIL, style, alignment })
  const overlap = getHeader({ sortId: COLUMN.OVERLAP, style, alignment })

  const array = [name]

  if (props.maxLseData)
    array.push(index)

  array.push(via)
  array.push(appointed)

  // NB: The resigned column will be set if past or notice checkboxes are set
  if (!!(orderSet & TABLE_BIT.RESIGNED))
    array.push(resigned)

  return [...array, overlap]
}
//#endregion

/***** details table *****/
const detailsTable = props => {
  let bodyArray = [
    [{
      text: [
        { text: `${JARGON.COMPANY_TIMELINE.FOUNDED}: `, bold: "true" },
        { text: getProp(props.dataItems.core, JARGON.COMPANY_TIMELINE.FOUNDED.toLowerCase()), color: greyText }
      ]
    }],
    [{
      text: [
        { text: `${JARGON.INCORPORATED} in: `, bold: "true" },
        { text: COUNTRY[props.hubData.companyRegCountry].name, color: greyText }
      ]
    }],
    [{
      text: [
        { text: "Company no.: ", bold: "true" },
        { text: props.hubData.companyNumber, color: greyText }
      ]
    }],
    [{
      text: [
        { text: "Registered address: ", bold: "true" },
        { text: props.hubData.fullAddress, color: greyText }
      ]
    }],
    [{
      text: [
        { text: "Previous names: ", bold: "true" },
        { text: getProp(props.dataItems.details, "previous names"), color: greyText }
      ]
    }],
    [{
      text: [
        { text: "Activities: ", bold: "true" },
        { text: getProp(props.dataItems.details, "activities"), color: greyText }
      ]
    }],
    // [{ text: " " }],
  ]

  if (props.hubData.lseProps) {
    const lseProps = props.hubData.lseProps

    bodyArray.push(
      [{
        text: [
          { text: "Company type: ", bold: "true" },
          {
            text: lseProps ?
              `${lseProps.aic ? JARGON.COMPANY_TYPE.INVEST : JARGON.COMPANY_TYPE.TRADE} company` : "?",
            color: greyText
          }
        ]
      }],
    )

    bodyArray.push(
      [{
        text: [
          { text: `${JARGON.ICB_INDUSTRY}: `, bold: "true" },
          { text: getCheckboxName(CHECKBOX[HOOK.INDUSTRY], props.hubData.lseProps.industryBit), color: greyText }
        ]
      }],
    )
  }

  bodyArray.push([{ text: " " }])

  return ({
    style: "detailsTable",
    layout: tableLayouts.details,
    table: {
      headerRows: 1,
      widths: ["*"],
      body: bodyArray,
    },
  })
}

const getProp = (array, id) => {
  // console.log("getProp id:", id)
  const obj = (array.find(obj => obj.id === id))
  return obj ? obj.value : JARGON.UNKNOWN
}

const getAgeObj = (personInfo, alignment) =>
  ({ text: personInfo ? getAge(personInfo.dateOfBirth) : JARGON.UNKNOWN, alignment })

const getSexText = personInfo => personInfo
  ? personInfo.sex === 0
    ? "M"
    : personInfo.sex === 1
      ? "F"
      : JARGON.UNKNOWN
  : JARGON.UNKNOWN

const getSexObj = (personInfo, alignment) =>
({
  text: getSexText(personInfo),
  alignment
})

const companiesHouseLink = hubData => hubData.companyRegCountry === "GB" && hubData.companyNumber
  ? `https://find-and-update.company-information.service.gov.uk/company/${hubData.companyNumber}`
  : null

/* supporting functions */
const getName = ({ hubData }) => hubData.lseProps ? nameAndLogo(hubData) : nameOnly(hubData)
const nameAndLogo = hubData => [{ text: hubData.name }, "", ""]
const nameOnly = hubData => [{ text: hubData.name }, "", ""]
const lastAccountsLink = hubData => hubData.lseProps?.annualReport?.url ?? hubData.pdfLink
const directorsCount = props => props.nodes.length - 1

export default cheatSheetPdf


